import React from 'react';
import { ArrowRight, Sparkles, X } from 'lucide-react';
import { motion } from 'framer-motion';

export const SERIF = { fontFamily: '"Instrument Serif", "Playfair Display", Georgia, serif' };

export const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
export const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };

export const H2 = 'font-[Manrope] font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-[-0.03em] leading-[1.05] text-slate-900';
export const SCROLL_MT = 'scroll-mt-[calc(var(--lp-top,72px)+0.75rem)]';

/* Text wrapped in *asterisks* renders as an italic serif highlight. */
export function Highlight({ text, dark = false, className = '' }) {
  if (!text) return null;
  const parts = String(text).split(/(\*[^*]+\*)/g).filter(Boolean);
  return (
    <span className={className}>
      {parts.map((p, i) => (p.startsWith('*') && p.endsWith('*') && p.length > 2
        ? <em key={i} className={`not-italic ${dark ? 'text-amber-400' : 'text-primary'}`} style={{ ...SERIF, fontStyle: 'italic', fontWeight: 400, fontSize: '1.06em' }}>{p.slice(1, -1)}</em>
        : <React.Fragment key={i}>{p}</React.Fragment>))}
    </span>
  );
}

export function Kicker({ children, dark = false }) {
  if (!children) return null;
  return (
    <span data-testid="section-kicker" className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[11.5px] font-bold tracking-[0.14em] uppercase mb-5 ${dark ? 'bg-white/10 text-amber-300 border border-white/10' : 'bg-white border border-amber-200/80 shadow-sm text-amber-700'}`}>
      <Sparkles size={12} />{children}
    </span>
  );
}

export function SectionHead({ label, title, subtitle, align = 'center', dark = false, className = '' }) {
  const center = align === 'center';
  return (
    <div className={`${center ? 'text-center mx-auto' : 'text-left'} max-w-3xl ${className}`}>
      <Kicker dark={dark}>{label}</Kicker>
      <h2 className={`${H2} ${dark ? '!text-white' : ''}`}><Highlight text={title} dark={dark} /></h2>
      {subtitle && <p className={`mt-4 text-[15px] sm:text-lg leading-relaxed ${dark ? 'text-white/70' : 'text-slate-600'} ${center ? 'mx-auto' : ''} max-w-2xl`}>{subtitle}</p>}
    </div>
  );
}

const VARIANTS = {
  blue: 'bg-primary hover:bg-primary-700 text-white shadow-[0_20px_45px_-14px_rgba(37,99,235,0.6)]',
  dark: 'bg-slate-900 hover:bg-slate-800 text-white shadow-[0_20px_45px_-14px_rgba(15,23,42,0.55)]',
  amber: 'bg-amber-500 hover:bg-amber-400 text-slate-900 shadow-[0_18px_36px_-10px_rgba(245,158,11,0.6)]',
};
export function CtaButton({ label, onClick, variant = 'blue', size = 'lg', className = '', testId }) {
  const pad = size === 'lg' ? 'px-8 sm:px-10 py-4 text-base sm:text-lg' : size === 'sm' ? 'px-4 py-2 text-[13px]' : 'px-7 py-3.5 text-[15px]';
  const circle = size === 'sm' ? 'w-6 h-6' : 'w-8 h-8';
  return (
    <button onClick={onClick} data-testid={testId}
      className={`group inline-flex items-center justify-center gap-3 rounded-full font-extrabold uppercase tracking-[0.04em] transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 ${pad} ${VARIANTS[variant]} ${className}`}>
      {label}
      <span className={`${circle} rounded-full flex items-center justify-center shrink-0 ${variant === 'amber' ? 'bg-slate-900/10' : 'bg-white/15'} transition-transform group-hover:translate-x-1`}>
        <ArrowRight size={size === 'sm' ? 12 : 16} />
      </span>
    </button>
  );
}

const stop = e => e.preventDefault();

/* Repeating diagonal watermark as an SVG tile — cheap, crisp at any size, sits over the image. */
function watermarkTile(text) {
  const t = String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;');
  // white text with a dark outline → readable on light score reports AND dark WhatsApp screenshots
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='260' height='170'><text x='130' y='90' font-family='Manrope,Arial,sans-serif' font-size='15' font-weight='700' fill='rgba(255,255,255,0.55)' stroke='rgba(15,23,42,0.6)' stroke-width='1.2' paint-order='stroke' stroke-linejoin='round' text-anchor='middle' transform='rotate(-24 130 90)'>${t}</text></svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
}

/* Image that resists right-click / drag / long-press saving and carries the copyright watermark.
   A phone screenshot can never be blocked — this only deters casual re-use. */
export function ProtectedImage({ src, alt, protection, className = '', imgClassName = '', style, testId, children }) {
  const on = protection?.enabled !== false;
  const wm = on ? (protection?.watermark_text ?? '© celpipmax.com') : '';
  return (
    <div className={`relative overflow-hidden select-none ${className}`} style={{ WebkitTouchCallout: 'none', ...style }}
      onContextMenu={on ? stop : undefined} onDragStart={on ? stop : undefined} data-testid={testId} data-protected={on ? 'true' : 'false'}>
      <img src={src} alt={alt || ''} loading="lazy" draggable={false} className={`${imgClassName} ${on ? 'pointer-events-none select-none' : ''}`} />
      {children}
      {wm && (
        <>
          <span className="absolute inset-0" style={{ backgroundImage: watermarkTile(wm), opacity: 0.85 }} aria-hidden="true" data-testid="image-watermark" />
          <span className="absolute bottom-1.5 right-2 px-1.5 py-0.5 rounded bg-slate-900/75 text-white text-[9.5px] font-semibold tracking-wide ring-1 ring-white/40" aria-hidden="true">{wm}</span>
        </>
      )}
      {on && <span className="absolute inset-0" aria-hidden="true" />}
    </div>
  );
}

export function Lightbox({ src, alt, onClose, protection }) {
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-[90] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="landing-lightbox">
      <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
        <ProtectedImage src={src} alt={alt || 'Enlarged'} protection={protection} className="rounded-2xl bg-white" imgClassName="w-full max-h-[84vh] object-contain rounded-2xl" />
        <button onClick={onClose} className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-white text-slate-900 flex items-center justify-center shadow-lg" data-testid="landing-lightbox-close"><X size={18} /></button>
      </div>
    </div>
  );
}

export function Reveal({ children, delay = 0, className = '', ...rest }) {
  return (
    <motion.div initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }} className={className} {...rest}>
      {children}
    </motion.div>
  );
}

export function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return true;
}
